/**
 * @fileoverview Shared field primitives for the three sign-in tabs.
 *
 * The instructor, student and organization forms are near-identical
 * email + password forms, and after the tab strip they sit in the SAME place on
 * screen — so any difference in padding, border or label casing reads as the
 * page jumping when you switch tabs. One set of primitives is what makes the
 * switch look like a highlight moving rather than a page reload.
 *
 * On Studio Quiet tokens (--studio-*, --text-*, --accent-yellow, --state-*),
 * not the raw palette the legacy auth screens carried.
 */

import { useState } from "react";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/** The bordered panel every sign-in form's fields live in. */
export const AuthCard = ({ className, children }) => (
  <div
    className={cn(
      "space-y-4 rounded-2xl border border-[var(--studio-divider)] bg-[var(--studio-raised)] p-6",
      className
    )}
  >
    {children}
  </div>
);

const FIELD_CLS =
  "mt-1 bg-[var(--studio-interactive)] text-[var(--text-primary)] " +
  "placeholder:text-[var(--text-quaternary)]";

/**
 * A labelled text field with optional inline error and password reveal.
 *
 * `error` renders under the field with `data-testid={errorTestId}` — the forms
 * surface validation inline rather than only as a toast, so a mistake is
 * explained next to the thing that caused it.
 */
export const AuthField = ({
  label,
  type = "text",
  value,
  onChange,
  placeholder,
  autoComplete,
  minLength,
  required,
  error,
  errorTestId,
  hint,
  hintTestId,
  reveal = false,
  revealSubject = "password",
  testId,
  children,
}) => {
  const [revealed, setRevealed] = useState(false);
  const inputType = reveal && revealed ? "text" : type;
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-[var(--text-tertiary)]">
        {label}
      </Label>
      <div className={reveal ? "relative" : undefined}>
        <Input
          type={inputType}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          autoComplete={autoComplete}
          minLength={minLength}
          required={required}
          data-testid={testId}
          aria-invalid={error ? "true" : undefined}
          className={cn(
            FIELD_CLS,
            reveal && "pr-10",
            error
              ? "border-[var(--state-danger)]"
              : "border-[var(--studio-divider-strong)]"
          )}
        />
        {reveal && (
          <button
            type="button"
            onClick={() => setRevealed((v) => !v)}
            aria-label={`${revealed ? "Hide" : "Show"} ${revealSubject}`}
            aria-pressed={revealed}
            className="absolute right-3 top-1/2 mt-0.5 -translate-y-1/2 text-[var(--text-tertiary)]
                       transition-colors hover:text-[var(--text-primary)]"
          >
            {revealed ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        )}
      </div>
      {error && (
        <p className="mt-1 text-xs text-[var(--state-danger)]" data-testid={errorTestId}>
          {error}
        </p>
      )}
      {!error && hint && (
        <p className="mt-1 text-xs text-[var(--text-tertiary)]" data-testid={hintTestId}>
          {hint}
        </p>
      )}
      {children}
    </div>
  );
};

/** The yellow primary submit every tab ends with. */
export const AuthSubmit = ({ submitting, icon: Icon, children, testId }) => (
  <Button
    type="submit"
    disabled={submitting}
    data-testid={testId}
    className="w-full rounded-full bg-[var(--accent-yellow)] py-3 text-sm font-medium
               text-[var(--text-inverse)] transition-transform hover:scale-105
               active:scale-95 disabled:opacity-50"
  >
    {submitting ? (
      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
    ) : (
      Icon && <Icon className="mr-2 h-4 w-4" />
    )}
    {children}
  </Button>
);

/**
 * The small print under a form (invite prompts, mode toggles, sales links).
 *
 * `as` exists for one real case: the ToS acceptance copy sits inside a
 * `<label>`, whose content model is phrasing content — a `<p>` there is invalid
 * nesting, so that caller passes "span".
 */
export const AuthNote = ({ as: Tag = "p", className, children, ...rest }) => (
  <Tag className={cn("text-xs leading-relaxed text-[var(--text-tertiary)]", className)} {...rest}>
    {children}
  </Tag>
);

/** An inline link inside AuthNote / consent copy. */
export const authLinkCls = "text-[var(--accent-yellow-text)] hover:underline";
